// Copyright 2020 Sorama B.V.

import {
    autoinject,
    bindable,
    BindingEngine,
    bindingMode,
    computedFrom,
    customElement,
    Disposable,
    TaskQueue,
} from "aurelia-framework";
import { IPagedRequest } from "libraries/WebRequestHandler/IWebRequestHandler";

@customElement("infinite-scroll")
@autoinject
export class InfiniteScroll {
    //#region Bindable Properties
    @bindable
    public pagedRequest: IPagedRequest<unknown>;

    @bindable({ defaultValue: 10 })
    public remainingItemsLoadThreshold: number;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    public filterText = "";

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public namePropertyName = "Name";

    @bindable({ defaultBindingMode: bindingMode.oneWay })
    public linePropertyNames: string[] = [];
    //#endregion

    //#region Computed Properties
    @computedFrom("scrollContainer.scrollHeight", "scrollContainer.scrollTop", "scrollContainer.offsetHeight")
    protected get scrollPercentage(): number {
        if (!this.scrollContainer || this.scrollContainer.scrollHeight === 0) return 1;

        return (this.scrollContainer.scrollTop + this.scrollContainer.offsetHeight) / this.scrollContainer.scrollHeight;
    }

    //#endregion

    //#region Elements
    private scrollContainer: HTMLElement;
    //#endregion

    //#region Private Properties
    private loadPromise: Promise<void>;

    private scrollBinding: Disposable;
    //#endregion

    //#region Constructor and Aurelia Callbacks
    constructor(
        private readonly bindingEngine: BindingEngine,
        private readonly taskQueue: TaskQueue
    ) {}

    protected bind(): void {
        this.scrollBinding = this.bindingEngine
            .propertyObserver(this, "scrollPercentage")
            .subscribe(this.scrollPercentageChanged);

        this.pagedRequestChanged(this.pagedRequest);
    }

    protected unbind(): void {
        this.scrollBinding.dispose();
    }

    //#endregion

    //#region Callbacks

    protected pagedRequestChanged(newPagedRequest: IPagedRequest<unknown>): void {
        if (newPagedRequest) {
            // Start loading it, ignore any existing load promise
            this.loadNext();
        }
    }

    private scrollPercentageChanged = (newPercentage: number): void => {
        if (this.loadPromise || this.pagedRequest.finished) {
            // Already loading, ignore this update
            return;
        }

        if ((1 - newPercentage) * this.pagedRequest.total.length < this.remainingItemsLoadThreshold) {
            this.loadNext();
        }
    };

    //#endregion

    //#region Helper Methods

    private loadNext(): void {
        if (this.loadPromise) {
            return;
        }

        let loadPromise: Promise<void>;
        this.loadPromise = loadPromise = this.pagedRequest
            .next()
            .catch((e) => {
                console.error(e);
            })
            .then(() => {
                // If the current load promise is still this promise, unset it
                if (this.loadPromise == loadPromise) {
                    this.loadPromise = undefined;
                }

                this.taskQueue.queueMicroTask(() => {
                    if (!this.pagedRequest.finished && this.isScrolledBottom()) {
                        this.loadNext();
                    }
                });
            });
    }

    protected clearSearch = (event: MouseEvent): void => {
        event.stopPropagation();
        this.filterText = "";
    };

    private isScrolledBottom(): boolean {
        return (1 - this.scrollPercentage) * this.pagedRequest.total.length < this.remainingItemsLoadThreshold;
    }

    //#endregion
}
